/**
 * The `[[` menu (`070` §7.1). The same `cmdk` primitive as the palette — "one primitive,
 * four uses" (`110` §6).
 *
 * `cmdk` filtering is switched OFF (`shouldFilter={false}`): the ranking already happened in
 * Postgres, across three legs, and re-filtering the eight rows it sent us with a client-side
 * fuzzy matcher would silently discard the trigram hits — the very rows that make
 * `[[sszelvin` find `Sszelvyn the Grey`.
 *
 * Rendered by `ReactRenderer` into the editor's own React tree, so it is keyboard-driven
 * through the suggestion plugin's `onKeyDown` rather than through the DOM: the caret stays
 * in the document the whole time, which is the entire point of a suggester.
 */
import { useEffect, useImperativeHandle, useState } from 'react';
import { FilePlus2, FileText } from 'lucide-react';
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandShortcut,
} from '@/components/ui/command';
import { useTranslation } from '@/lib/i18n';
import type { WikiLinkItem } from '@/features/editor/wikiLink';

export interface WikiLinkMenuHandle {
  /** Returns true when the key was consumed, which stops it reaching the document. */
  onKeyDown: (event: KeyboardEvent) => boolean;
}

export interface WikiLinkMenuProps {
  items: WikiLinkItem[];
  loading: boolean;
  query: string;
  command: (item: WikiLinkItem) => void;
  ref?: React.Ref<WikiLinkMenuHandle>;
}

function breadcrumbOf(item: Extract<WikiLinkItem, { kind: 'page' }>): string {
  // Truncates from the LEFT: the NEAREST ancestor is the informative one (`070` §4).
  const titles = item.breadcrumb.map((crumb) => crumb.title);
  return titles.length > 2 ? `… / ${titles.slice(-2).join(' / ')}` : titles.join(' / ');
}

export function WikiLinkMenu({ items, loading, query, command, ref }: WikiLinkMenuProps) {
  const { t } = useTranslation();
  const [active, setActive] = useState(0);

  // A new result set means the old index may point at nothing, or at something else. Reset
  // to the top — the same rule the palette follows on a mode switch (criterion 6).
  useEffect(() => {
    setActive(0);
  }, [items]);

  useImperativeHandle(
    ref,
    () => ({
      onKeyDown: (event: KeyboardEvent): boolean => {
        if (items.length === 0) return false;

        switch (event.key) {
          case 'ArrowDown':
            setActive((i) => (i + 1) % items.length);
            return true;
          case 'ArrowUp':
            setActive((i) => (i - 1 + items.length) % items.length);
            return true;
          case 'Enter': {
            const item = items[active];
            if (item === undefined) return false;
            command(item);
            return true;
          }
          default:
            return false;
        }
      },
    }),
    [active, command, items],
  );

  // `onExit` unmounts us on Esc, and the suggestion plugin leaves the literal `[[` text
  // alone — the escape hatch (`070` §7.1).
  if (items.length === 0) {
    return (
      <Command
        shouldFilter={false}
        className="w-80 rounded-lg border border-[var(--border)] shadow-md"
      >
        <div className="px-2 py-2 text-[var(--muted-foreground)]">
          {loading ? t('editor.wikiLink.searching') : t('editor.wikiLink.noMatch', { query })}
        </div>
      </Command>
    );
  }

  return (
    <Command
      shouldFilter={false}
      value={String(active)}
      className="w-80 rounded-lg border border-[var(--border)] shadow-md"
    >
      <CommandList>
        <CommandGroup>
          {items.map((item, i) => (
            <CommandItem
              key={item.kind === 'page' ? item.id : `create:${item.label}`}
              value={String(i)}
              onSelect={() => {
                command(item);
              }}
              // A mouse is never required (criterion 5), but it must not lie either: hovering
              // moves the keyboard selection, so Enter always activates what looks active.
              onMouseEnter={() => {
                setActive(i);
              }}
            >
              {item.kind === 'create' ? (
                <>
                  <FilePlus2 strokeWidth={1.75} />
                  <span className="truncate">
                    {t('editor.wikiLink.createPagePrefix')}
                    <span className="font-medium">{item.label}</span>
                    {t('editor.wikiLink.createPageSuffix')}
                  </span>
                </>
              ) : (
                <>
                  {item.icon === null ? (
                    <FileText strokeWidth={1.75} />
                  ) : (
                    <span aria-hidden className="size-[var(--icon)] text-center leading-none">
                      {item.icon}
                    </span>
                  )}
                  <span className="truncate">{item.title}</span>
                  {item.breadcrumb.length > 0 && (
                    <CommandShortcut className="truncate">{breadcrumbOf(item)}</CommandShortcut>
                  )}
                </>
              )}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}
